import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Col, Layout, Row, DatePicker } from 'antd';
import { capitalize, get, omit } from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import queryString from 'query-string';

import ResellerServices from 'services/reseller';

import { TableList } from 'components/table-list';
import PdButton from 'components/pd-button';

import { ProfileContext } from 'contexts/profile';

import { errHandling, priceFormat } from 'utils';

const { Content } = Layout;
const { RangePicker } = DatePicker;

export function CashbackResellers() {
  const location = useLocation();
  const { fetchProfile } = useContext(ProfileContext);

  const [orderList, setOrderList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [endDatePick, setEndDate] = useState('');
  const [loadStatus, setLoadStatus] = useState(true);
  const [nextPage, setNextPage] = useState(0);
  const [startDatePick, setStartDate] = useState('');

  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);
  const dateFormat = 'YYYY/MM/DD';

  const seeDetail = (row) => (
    <div className="button-table">
      <Link to={`/orders/package?id=${row.order.id}`}>
        <PdButton type="primary" style={{ height: 32, width: 100 }}>
          See Detail
        </PdButton>
      </Link>
    </div>
  );

  const columns = [
    {
      name: 'Date',
      selector: row => row.created_at,
      format: (row) => dayjs(row.created_at).format('DD/MM/YYYY'),
      width: '15%',
      center: true,
    },
    {
      name: 'Description',
      cell: () => {
        return 'Completed'; /* All description cells will always be 'Completed'*/
      },
      width: '15%',
      center: true,
    },
    {
      name: 'Amount',
      selector: row => row.amount,
      width: '15%',
      center: true,
      format: (row) => `IDR  ${priceFormat(row.amount)}`,
    },
    {
      name: 'Reseller Name',
      selector: row => row.reseller.first_name,
      center: true,
      width: '18%',
      format: (row) =>
        row.reseller && row.reseller.first_name && capitalize(row.reseller.first_name),
    },
    {
      name: 'Store Name',
      selector: row => row.reseller.store_name,
      center: true,
      width: '18%',
      format: (row) =>
        row.reseller && row.reseller.store_name && capitalize(row.reseller.store_name),
    },
    {
      name: '',
      cell: seeDetail,
      center: true,
      width: '18%',
    },
  ];

  async function fetchCashbackList({ page, startDate, endDate }) {
    setLoadStatus(true);
    try {
      let end = endDate;
      if (endDate) {
        end = dayjs(endDate, dateFormat).add(1, 'days');
        end = dayjs(end).format(dateFormat);
      }
      const { data } = await ResellerServices.getAllCashback({
        endDate: end,
        page,
        startDate,
      });
      const cashbackData = get(data, 'data', []);
      const cursor = get(data, 'metadata.next_cursor', -1);
      setOrderList(cashbackData);
      setNextPage(cursor);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/resellers_cashback',
      search: stringified,
    });
  }

  function onChangeDate(dates, [startDate, endDate]) {
    if (startDate === '') delete parsed['start_date'];
    else parsed['start_date'] = startDate;

    if (endDate === '') delete parsed['end_date'];
    else parsed['end_date'] = endDate;

    parsed['page'] = 1;

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/resellers_cashback',
      search: stringified,
    });
  }

  function resetFilter() {
    const stringified = queryString.stringify(
      omit(parsed, ['end_date', 'limit', 'page', 'start_date'])
    );
    navigate({
      pathname: '/resellers_cashback',
      search: stringified,
    });
  }

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* eslint-disable */
  useEffect(() => {
    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/resellers_cashback',
          search: stringified,
        });
      } else setCurrentPage(parsed['page']);
    } else setCurrentPage(1);

    if (parsed['start_date']) setStartDate(parsed['start_date']);
    else setStartDate('');

    if (parsed['end_date']) setEndDate(parsed['end_date']);
    else setEndDate('');

    fetchCashbackList({
      page: parsed['page'],
      startDate: parsed['start_date'],
      endDate: parsed['end_date'],
      status: parsed['order.status'],
    });
  }, [
    parsed['end_date'],
    parsed['page'],
    parsed['start_date'],
    parsed['order.status'],
  ]);
  /* eslint-enable */

  return (
    <Layout className="main-container">
      <div className="content">
        <div className="pageTitle">Cashback Reseller</div>
        <Content className="pageContent">
          <Row gutter={18}>
            <Col className="height-32" span={0}>
              <div className="invisible">invisible</div>
            </Col>
            <Col span={7} className="mt-1">
              <RangePicker
                className="w-full"
                format={dateFormat}
                onChange={onChangeDate}
                value={[
                  startDatePick && dayjs(startDatePick, dateFormat),
                  endDatePick && dayjs(endDatePick, dateFormat),
                ]}
              />
            </Col>
            <Col span={4} className="mt-1">
              <span className="reset-filter" onClick={resetFilter}>
                Reset Filter
              </span>
            </Col>
            <Col span={4} offset={8} className="height-32">
              <div className="invisible">invisible</div>
              <Link to="/resellers_cashback/create">
                <PdButton
                  type="primary"
                  full
                  outline
                  style={{ height: 32, width: 176 }}>
                  Change Cashback
                </PdButton>
              </Link>
            </Col>

            {!loadStatus && (
              <TableList
                changePage={changePage}
                columns={columns}
                loadingOrder={loadStatus}
                nextPage={nextPage}
                orderList={orderList}
                page={currentPage}
                responsive={false}
              />
            )}
          </Row>
        </Content>
      </div>
    </Layout>
  );
}

export default { CashbackResellers };
